import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { fetchAuditLogs } from '../../services/auditLogService';
import { supabase } from '../../lib/supabase';
import AuditFilters from './components/AuditFilters';
import AuditLogTable from './components/AuditLogTable';
import AuditStatsBar from './components/AuditStatsBar';

const DEFAULT_FILTERS = {
  dateFrom: '',
  dateTo: '',
  actionType: '',
  adminId: '',
  affectedUserSearch: '',
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [admins, setAdmins] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  useEffect(() => {
    loadAdmins();
  }, []);

  useEffect(() => {
    setPage(1);
    loadLogs(1);
  }, [filters]);

  useEffect(() => {
    if (page > 1) loadLogs(page);
  }, [page]);

  const loadAdmins = async () => {
    try {
      const { data } = await supabase?.from('user_profiles')?.select('id, full_name, email')?.eq('role', 'administrator');
      setAdmins(data || []);
    } catch (err) {
      console.error('Error loading admins:', err?.message);
    }
  };

  const loadLogs = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const actionTypes = filters?.actionType ? [filters?.actionType] : [];
      const { data, count } = await fetchAuditLogs({
        dateFrom: filters?.dateFrom || undefined,
        dateTo: filters?.dateTo || undefined,
        actionTypes: actionTypes?.length > 0 ? actionTypes : undefined,
        adminId: filters?.adminId || undefined,
        affectedUserSearch: filters?.affectedUserSearch || undefined,
        page: pageNum,
        pageSize: PAGE_SIZE,
      });
      setLogs(data);
      setTotalCount(count);
    } catch (err) {
      console.error('Error loading audit logs:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const handleExportCSV = () => {
    if (!logs?.length) return;
    const headers = ['Timestamp', 'Action Type', 'Performed By', 'Performed By Email', 'Affected User', 'Affected User Email', 'Description'];
    const rows = logs?.map(log => [
      new Date(log.created_at)?.toLocaleString(),
      log?.action_type,
      log?.performer?.full_name || '',
      log?.performer?.email || '',
      log?.affected?.full_name || log?.affected_user_email || '',
      log?.affected?.email || '',
      log?.description || '',
    ]);
    const csvContent = [headers, ...rows]?.map(r => r?.map(c => `"${String(c)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="ClipboardList" size={24} className="text-accent" />
              </div>
              Audit Log
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Track all administrative actions and system changes for compliance and security oversight
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={handleExportCSV}
              disabled={!logs?.length}
            >
              Export CSV
            </Button>
            <Button
              variant="ghost"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={() => loadLogs(page)}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        {!loading && logs?.length > 0 && <AuditStatsBar logs={logs} />}

        {/* Filters */}
        <AuditFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          admins={admins}
          onReset={handleResetFilters}
        />

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {loading ? 'Loading...' : `Showing ${logs?.length} of ${totalCount} entries`}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              >
                <Icon name="ChevronLeft" size={16} />
              </Button>
              <span className="text-sm text-foreground px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading}
              >
                <Icon name="ChevronRight" size={16} />
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <AuditLogTable logs={logs} loading={loading} />
      </div>
    </MainLayout>
  );
};

export default AuditLog;
